import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import {
    updateManagementTask,
    getManagementTaskById
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { TaskStatus, statusOptionsForEdit, isWorkStarted, toDateOnly } from './taskWorkflow';
import { childDueAfterParent } from './taskTree';
import DueDateReasonModal from './DueDateReasonModal';
import { assigneeKindFromApi, ASSIGNEE_REQUIRED_MESSAGE, assigneePayload, hasAssignee } from './assigneeKind';
import { allocationExceedsBudget, ALLOCATION_EXCEEDED_MESSAGE, unallocated } from './budget';
import {
    editKindOptions,
    TaskKind,
    usesPermissionFlowKind,
    PermissionStage,
    SUBTASK_TYPE_REQUIRED_MESSAGE,
    COMPLETE_WITHOUT_APPROVAL_MESSAGE,
    PERMISSION_END_BEFORE_START_MESSAGE,
    AUTHORITY_REQUIRED_MESSAGE,
    APPLICATION_NUMBER_REQUIRED_MESSAGE,
    permissionEndBeforeStart,
    missingPermissionAuthority,
    missingPermissionApplicationNumber,
    canStartPermissionSubtask,
    permissionStartBlockedMessage
} from './permission';
import { actsAsOwner } from './ownership';

// On a project page the task's project is already settled — hide the picker
// rather than offer moving the task somewhere else from here.
const EditTaskModal = ({ onClose, projects, task, lockedProjectId = null }) => {
    const queryClient = useQueryClient();
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(null);

    const { register, control, handleSubmit, watch, reset } = useForm({
        defaultValues: {
            title: '',
            description: '',
            project_id: '',
            priority: 'Medium',
            budget: '',
            status: TaskStatus.PLANNED,
            dueDate: '',
            kind: TaskKind.GENERAL,
            authority: '',
            applicationNumber: '',
            permissionStartDate: '',
            permissionEndDate: '',
            subtasks: []
        }
    });

    // Subtasks aren't edited here any more (use Edit Subtask on the task page),
    // but they stay in the form so Update Task sends them back unchanged.
    useFieldArray({
        control,
        name: "subtasks"
    });

    const { data: taskDetails, refetch: refetchTaskDetails } = useQuery({
        queryKey: ['managementTaskDetails', task?.id],
        queryFn: () => getManagementTaskById(task.id),
        enabled: !!task?.id
    });

    const projectOptions = projects?.map(p => ({
        label: p.label,
        value: p.value
    })) || [];

    const priorityOptions = [
        { label: 'High', value: 'High' },
        { label: 'Medium', value: 'Medium' },
        { label: 'Low', value: 'Low' }
    ];

    const taskData = taskDetails?.data;
    // A court case's status, target date and type are changed on its Case tab
    // (PUT /task/:id/legal); the generic PUT refuses them.
    const isLegalCase = taskData?.kind === TaskKind.LEGAL_CASE;

    useEffect(() => {
        if (taskData) {
            reset({
                title: taskData.title,
                description: taskData.description,
                project_id: taskData.project_id,
                priority: taskData.priority,
                budget: taskData.budget,
                status: taskData.status,
                dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString().split('T')[0] : '',
                kind: taskData.kind || TaskKind.GENERAL,
                authority: taskData.authority || '',
                applicationNumber: taskData.applicationNumber || '',
                permissionStartDate: toDateOnly(taskData.permissionStartDate),
                permissionEndDate: toDateOnly(taskData.permissionEndDate),
                subtasks: (taskData.subtasks || taskData.subTask)?.map(st => ({
                    id: st.id,
                    title: st.title,
                    description: st.description,
                    status: st.status,
                    assignedTo_id: st.assignedTo_id,
                    assigneeKind: assigneeKindFromApi(st),
                    assignedTo_sp_id: st.assignedTo_sp_id || '',
                    assignedTo_vendor_id: st.assignedTo_vendor_id || '',
                    approver_id: st.approver_id,
                    permissionType: st.permissionType || '',
                    duration: st.duration,
                    budget: st.budget ?? '',
                    plannedStartDate: st.plannedStartDate ? new Date(st.plannedStartDate).toISOString().split('T')[0] : '',
                    plannedEndDate: st.plannedEndDate ? new Date(st.plannedEndDate).toISOString().split('T')[0] : '',
                    parent_subtask_id: null,
                    dueDateOverrideReason: st.dueDateOverrideReason || '',
                    docs: st.docs || [],
                    expenses: st.expenses || st.sub_task_expenses || []
                })) || []
            });
        }
    }, [taskData, reset]);

    const invalidateTaskQueries = () => {
        queryClient.invalidateQueries(['managementTasks']);
        queryClient.invalidateQueries(['managementTask']);
        queryClient.invalidateQueries(['managementTaskDetails', task.id]);
        refetchTaskDetails();
    };

    const updateMutation = useMutation({
        mutationFn: (payload) => updateManagementTask(task.id, payload),
        onSuccess: () => {
            toast.success('Task updated successfully');
            invalidateTaskQueries();
            onClose();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update task');
        }
    });

    const buildPayload = (data) => {
        // Field is not rendered when locked, so do not rely on it round-tripping
        // through handleSubmit — fall back to the task's own project.
        const effectiveProjectId =
            lockedProjectId ?? data.project_id ?? taskData?.project_id;
        const payload = {
        title: data.title,
        description: data.description,
        project_id: Number(effectiveProjectId),
        priority: data.priority,
        budget: data.budget === '' || data.budget == null ? null : Number(data.budget),
        status: data.status,
        dueDate: data.dueDate,
        subtasks: data.subtasks.map(st => {
            const numericId = Number(st.id);
            const id =
                Number.isInteger(numericId) &&
                numericId > 0 &&
                !String(st.id).startsWith('task-')
                    ? numericId
                    : undefined;
            return {
                id,
                title: st.title,
                description: st.description,
                ...assigneePayload(st.assigneeKind || 'employee', st),
                project_id: effectiveProjectId ? Number(effectiveProjectId) : null,
                approver_id: st.approver_id ? Number(st.approver_id) : null,
                permissionType:
                    usesPermissionFlowKind(data.kind || taskData?.kind)
                        ? st.permissionType
                        : undefined,
                duration: Number(st.duration),
                budget: st.budget === '' || st.budget == null ? null : Number(st.budget),
                status: st.status,
                plannedStartDate: st.plannedStartDate,
                plannedEndDate: st.plannedEndDate,
                parent_subtask_id: null,
                dueDateOverrideReason: st.dueDateOverrideReason || undefined,
                docs: st.docs || [],
                expenses: st.expenses || []
            };
        })
        };
        const isOwner = actsAsOwner(taskData);
        if (isLegalCase) {
            // The case endpoint owns these; don't echo them to the generic PUT.
            delete payload.status;
            delete payload.dueDate;
            // No budget field on a court case; leave whatever is stored alone.
            delete payload.budget;
        } else if (isOwner) {
            payload.kind = data.kind || TaskKind.GENERAL;
            if (usesPermissionFlowKind(payload.kind)) {
                payload.authority = data.authority || null;
                payload.applicationNumber = data.applicationNumber || null;
                payload.permissionStartDate = data.permissionStartDate || null;
                payload.permissionEndDate = data.permissionEndDate || null;
            }
        }
        return payload;
    };

    const applyDueDateReasons = (payload, reason, originalSubs) => {
        const next = { ...payload, subtasks: payload.subtasks.map((st) => ({ ...st })) };
        if (isWorkStarted(taskData?.status) && toDateOnly(payload.dueDate) !== toDateOnly(taskData?.dueDate)) {
            next.dueDateChangeReason = reason;
        }
        next.subtasks = next.subtasks.map((st) => {
            const orig = originalSubs.find((o) => String(o.id) === String(st.id));
            const extra = {};
            if (
                orig &&
                isWorkStarted(orig.status) &&
                toDateOnly(st.plannedEndDate) !== toDateOnly(orig.plannedEndDate)
            ) {
                extra.dueDateChangeReason = reason;
            }
            if (childDueAfterParent(st.plannedEndDate, payload.dueDate)) {
                extra.dueDateOverrideReason = reason;
            }
            return Object.keys(extra).length ? { ...st, ...extra } : st;
        });
        return next;
    };

    const onSubmit = (data) => {
        if (allocationExceedsBudget(data.budget, data.subtasks, null)) {
            toast.error(ALLOCATION_EXCEEDED_MESSAGE);
            return;
        }
        const missingAssignee = (data.subtasks || []).findIndex(
            (st) => !hasAssignee(st.assigneeKind || 'employee', st)
        );
        if (missingAssignee >= 0) {
            toast.error(`${ASSIGNEE_REQUIRED_MESSAGE} for subtask #${missingAssignee + 1}`);
            return;
        }
        if (usesPermissionFlowKind(data.kind || taskData?.kind)) {
            const missingType = (data.subtasks || []).findIndex((st) => !st.permissionType);
            if (missingType >= 0) {
                toast.error(`${SUBTASK_TYPE_REQUIRED_MESSAGE} — subtask #${missingType + 1}`);
                return;
            }
            const ownerEditing =
                actsAsOwner(taskData);
            if (ownerEditing && missingPermissionAuthority(data.authority)) {
                toast.error(AUTHORITY_REQUIRED_MESSAGE);
                return;
            }
            if (ownerEditing && missingPermissionApplicationNumber(data.applicationNumber)) {
                toast.error(APPLICATION_NUMBER_REQUIRED_MESSAGE);
                return;
            }
            if (permissionEndBeforeStart(data.permissionStartDate, data.permissionEndDate)) {
                toast.error(PERMISSION_END_BEFORE_START_MESSAGE);
                return;
            }
        }
        if (
            data.status === TaskStatus.COMPLETED &&
            usesPermissionFlowKind(data.kind || taskData?.kind) &&
            taskData?.permissionStage !== PermissionStage.APPROVAL
        ) {
            if (!window.confirm(COMPLETE_WITHOUT_APPROVAL_MESSAGE)) return;
        }
        const payload = buildPayload(data);
        const originalSubs = taskData?.subtasks || taskData?.subTask || [];
        if (usesPermissionFlowKind(data.kind || taskData?.kind)) {
            const blockedStart = (data.subtasks || []).find((st) => {
                if (!isWorkStarted(st.status)) return false;
                const orig = originalSubs.find((o) => String(o.id) === String(st.id));
                if (orig && String(orig.status) === String(st.status)) return false;
                return !canStartPermissionSubtask(taskData, st);
            });
            if (blockedStart) {
                toast.error(permissionStartBlockedMessage(blockedStart.permissionType));
                return;
            }
        }
        const rootNeedsReason =
            isWorkStarted(taskData?.status) &&
            toDateOnly(data.dueDate) !== toDateOnly(taskData?.dueDate);
        const nestedNeedReason = data.subtasks.some((st) => {
            const orig = originalSubs.find((o) => String(o.id) === String(st.id));
            if (!orig) return false;
            return isWorkStarted(orig.status) && toDateOnly(st.plannedEndDate) !== toDateOnly(orig.plannedEndDate);
        });
        // A court case's actions are not capped by its target date.
        const nestedNeedOverride = !isLegalCase && data.subtasks.some((st) => {
            if (!childDueAfterParent(st.plannedEndDate, data.dueDate)) return false;
            const orig = originalSubs.find((o) => String(o.id) === String(st.id));
            if (!orig) return true;
            if (toDateOnly(st.plannedEndDate) !== toDateOnly(orig.plannedEndDate)) return true;
            return !String(orig.dueDateOverrideReason || '').trim();
        });

        if (rootNeedsReason || nestedNeedReason || nestedNeedOverride) {
            setPendingSubmit({ payload, originalSubs, nestedNeedOverride });
            setShowReasonModal(true);
            return;
        }
        updateMutation.mutate(payload);
    };

    const remaining = unallocated(watch('budget'), watch('subtasks') || [], null);
    const kind = watch('kind');
    const isOwner = actsAsOwner(taskData);

    return (
        <>
            <Modal
                name="Edit Task"
                width={70}
                onClose={onClose}
                hideClose={false}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <FormInput
                            label="Title"
                            register={register}
                            register_key="title"
                            type="text"
                            isRequired={true}
                            width={100}
                        />
                        {!lockedProjectId && (
                            <FormInput
                                label="Project"
                                register={register}
                                register_key="project_id"
                                type="dropdown"
                                options={projectOptions}
                                isRequired={true}
                                width={100}
                            />
                        )}
                        <div className="col-span-2">
                            <FormInput
                                label="Description"
                                register={register}
                                register_key="description"
                                type="text"
                                isRequired={true}
                                width={100}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-start space-x-2 w-fit mb-2">
                                <span className="text-gray-600">Owner</span>
                            </div>
                            <div className="p-2 border border-stone-300 rounded bg-[#e7e5e4] text-gray-800">
                                {taskData?.owner?.name || '—'}
                            </div>
                        </div>
                        <FormInput
                            label="Priority"
                            register={register}
                            register_key="priority"
                            type="dropdown"
                            options={priorityOptions}
                            isRequired={true}
                            width={100}
                        />
                        {isLegalCase ? (
                            <div>
                                <div className="flex justify-between items-start space-x-2 w-fit mb-2">
                                    <span className="text-gray-600">Type</span>
                                </div>
                                <div className="p-2 border border-stone-300 rounded bg-[#e7e5e4] text-gray-800">
                                    Legal Court Case
                                </div>
                            </div>
                        ) : (
                            <FormInput
                                label="Status"
                                register={register}
                                register_key="status"
                                type="dropdown"
                                options={statusOptionsForEdit(taskData?.status)}
                                isRequired={true}
                                width={100}
                            />
                        )}
                        {isOwner && !isLegalCase && (
                            <>
                                <FormInput
                                    label="Type"
                                    register={register}
                                    register_key="kind"
                                    type="dropdown"
                                    options={editKindOptions}
                                    isRequired={true}
                                    width={100}
                                />
                                {usesPermissionFlowKind(kind) && (
                                    <>
                                        <FormInput
                                            label="Authority"
                                            register={register}
                                            register_key="authority"
                                            type="text"
                                            isRequired={true}
                                            width={100}
                                        />
                                        <FormInput
                                            label="Application no."
                                            register={register}
                                            register_key="applicationNumber"
                                            type="text"
                                            isRequired={true}
                                            width={100}
                                        />
                                        <FormInput
                                            label="Start Date"
                                            register={register}
                                            register_key="permissionStartDate"
                                            type="date"
                                            width={100}
                                        />
                                        <FormInput
                                            label="End Date"
                                            register={register}
                                            register_key="permissionEndDate"
                                            type="date"
                                            width={100}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {/* A court case's expenses go to Accounts and never count against a budget. */}
                        {!isLegalCase && (
                            <FormInput
                                label="Budget (₹)"
                                register={register}
                                register_key="budget"
                                type="number"
                                width={100}
                            />
                        )}
                        {isLegalCase ? (
                            <div>
                                <div className="flex justify-between items-start space-x-2 w-fit mb-2">
                                    <span className="text-gray-600">Target date</span>
                                </div>
                                <div className="p-2 border border-stone-300 rounded bg-[#e7e5e4] text-gray-800">
                                    {toDateOnly(taskData?.dueDate) || '—'}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Case status, target date and case details are edited on the Case tab.
                                </p>
                            </div>
                        ) : (
                            <FormInput
                                label="Due Date"
                                register={register}
                                register_key="dueDate"
                                type="date"
                                isRequired={true}
                                width={100}
                            />
                        )}
                        <p className="text-xs text-gray-500 col-span-2">
                            Unallocated: ₹{remaining.toLocaleString()}
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e]"
                        >
                            Update Task
                        </Button>
                    </div>
                </form>
            </Modal>
            {showReasonModal && (
                <DueDateReasonModal
                    title={pendingSubmit?.nestedNeedOverride ? 'Reason for due date' : undefined}
                    body={
                        pendingSubmit?.nestedNeedOverride
                            ? 'A child due date is after the parent due date. Enter a reason to save this change.'
                            : undefined
                    }
                    onClose={() => {
                        setShowReasonModal(false);
                        setPendingSubmit(null);
                    }}
                    onConfirm={(reason) => {
                        if (!pendingSubmit) return;
                        const next = applyDueDateReasons(
                            pendingSubmit.payload,
                            reason,
                            pendingSubmit.originalSubs
                        );
                        setShowReasonModal(false);
                        setPendingSubmit(null);
                        updateMutation.mutate(next);
                    }}
                />
            )}
        </>
    );
};

export default EditTaskModal;
